import React, { useState, useEffect, useMemo, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users, Mail, Check, X, ChevronRight, Shield, Search,
  Flame, Trophy, Filter
} from 'lucide-react';
import { authAPI } from '../api';
import { AuthContext } from '../AuthContext';
import './TeamsPage.css';

function SkeletonRow() {
  return <div className="lp-skeleton tms-skeleton-row" />;
}

export default function TeamsPage() {
  const { isAuthenticated } = useContext(AuthContext);
  const [allTeams, setAllTeams]       = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy]           = useState('name_asc');
  const [sizeFilter, setSizeFilter]   = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  useEffect(() => { loadData(); }, [isAuthenticated]); // eslint-disable-line

  const loadData = async () => {
    setLoading(true);
    try {
      const allRes = await authAPI.getAllTeams();
      setAllTeams(allRes.data || []);

      if (isAuthenticated) {
        try {
          const invRes = await authAPI.getPendingInvitations();
          setInvitations(invRes.data || []);
        } catch { /* ignore if not authenticated */ }
      }
    } catch {
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else          { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  const handleAcceptInvitation = async (token) => {
    try {
      await authAPI.acceptInvitation(token);
      showMessage('Invitation acceptée !');
      loadData();
    } catch (err) {
      showMessage(err.message || 'Erreur', true);
    }
  };

  const handleDeclineInvitation = async (token) => {
    try {
      await authAPI.declineInvitation(token);
      showMessage('Invitation refusée');
      loadData();
    } catch (err) {
      showMessage(err.message || 'Erreur', true);
    }
  };

  const filteredTeams = useMemo(() => {
    let result = allTeams.filter(t =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.tag && t.tag.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    if (sizeFilter === 'open') result = result.filter(t => (t.memberCount || 0) < 5);
    if (sizeFilter === 'full') result = result.filter(t => (t.memberCount || 0) >= 5);
    result.sort((a, b) => {
      if (sortBy === 'name_asc')     return a.name.localeCompare(b.name, 'fr');
      if (sortBy === 'name_desc')    return b.name.localeCompare(a.name, 'fr');
      if (sortBy === 'members_desc') return (b.memberCount || 0) - (a.memberCount || 0);
      if (sortBy === 'members_asc')  return (a.memberCount || 0) - (b.memberCount || 0);
      return 0;
    });
    return result;
  }, [allTeams, searchQuery, sortBy, sizeFilter]);

  const hasActiveFilters = sortBy !== 'name_asc' || sizeFilter !== 'all';
  const openTeams = allTeams.filter(t => (t.memberCount || 0) < 5).length;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="tms-page">

      {/* ══════════════ HERO ══════════════ */}
      <div className="tms-hero">
        <div className="tms-hero-bg" />
        <div className="tms-hero-orbs">
          <div className="tms-hero-orb tms-hero-orb--1" />
          <div className="tms-hero-orb tms-hero-orb--2" />
        </div>
        <div className="tms-hero-content">
          <motion.div
            className="tms-hero-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="tms-hero-icon-row">
              <Shield size={32} className="tms-hero-shield" />
            </div>
            <h1>Équipes</h1>
            <p className="tms-hero-sub">
              Trouvez votre squad. Rejoignez une équipe ou créez la vôtre.
            </p>
            <div className="tms-hero-stats">
              <div className="tms-hero-stat">
                <span className="tms-hero-stat-num">{allTeams.length}</span>
                <span className="tms-hero-stat-label">Équipes</span>
              </div>
              <div className="tms-hero-stat-divider" />
              <div className="tms-hero-stat">
                <span className="tms-hero-stat-num">{openTeams}</span>
                <span className="tms-hero-stat-label">Recrutent</span>
              </div>
              {invitations.length > 0 && (
                <>
                  <div className="tms-hero-stat-divider" />
                  <div className="tms-hero-stat">
                    <span className="tms-hero-stat-num tms-hero-stat-num--teal">{invitations.length}</span>
                    <span className="tms-hero-stat-label">Invitation{invitations.length > 1 ? 's' : ''}</span>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ══════════════ TOASTS ══════════════ */}
      <AnimatePresence>
        {error && (
          <motion.div key="err" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="tms-toast tms-toast--error">
            <X size={15} /> {error}
          </motion.div>
        )}
        {success && (
          <motion.div key="ok" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="tms-toast tms-toast--success">
            <Check size={15} /> {success}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="tms-body">

        {/* ══════════════ INVITATIONS (auth only) ══════════════ */}
        <AnimatePresence>
          {isAuthenticated && invitations.length > 0 && (
            <motion.section
              key="invitations"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="tms-invitations"
            >
              <div className="tms-invitations__header">
                <Mail size={18} />
                <h2 className="tms-invitations__title">Invitations en attente</h2>
                <span className="tms-invitations__count">{invitations.length}</span>
              </div>

              <div className="tms-invitations__list">
                {invitations.map((inv, i) => (
                  <motion.div
                    key={inv._id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                    className="tms-inv-card"
                  >
                    <div className="tms-inv-card__icon">
                      <Shield size={20} />
                    </div>
                    <div>
                      <p className="tms-inv-card__team">{inv.team?.name}</p>
                      {inv.team?.tag && (
                        <span className="tms-inv-card__tag">[{inv.team.tag}]</span>
                      )}
                      <p className="tms-inv-card__from">
                        Invité par {inv.invitedBy?.displayName || inv.invitedBy?.username}
                      </p>
                    </div>
                    <div className="tms-inv-card__actions">
                      <button className="tms-inv-card__accept" onClick={() => handleAcceptInvitation(inv.invitationToken)}>
                        <Check size={14} /> Accepter
                      </button>
                      <button className="tms-inv-card__decline" onClick={() => handleDeclineInvitation(inv.invitationToken)}>
                        <X size={14} /> Refuser
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* ══════════════ TOOLBAR ══════════════ */}
        <div className="tms-toolbar">
          <div className="tms-toolbar-top">
            <div className="tms-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Rechercher une équipe..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button className="tms-search-clear" onClick={() => setSearchQuery('')}>
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              className={`tms-filter-toggle${showFilters ? ' active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={15} /> Filtres
              {hasActiveFilters && <span className="tms-filter-dot" />}
            </button>
          </div>

          {showFilters && (
            <motion.div
              className="tms-filter-controls"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
            >
              <select className="tms-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="name_asc">Nom A → Z</option>
                <option value="name_desc">Nom Z → A</option>
                <option value="members_desc">Membres (plus)</option>
                <option value="members_asc">Membres (moins)</option>
              </select>
              <select className="tms-select" value={sizeFilter} onChange={e => setSizeFilter(e.target.value)}>
                <option value="all">Taille : Toutes</option>
                <option value="open">Places disponibles</option>
                <option value="full">Complet (5/5)</option>
              </select>
              {hasActiveFilters && (
                <button className="tms-clear-btn" onClick={() => { setSortBy('name_asc'); setSizeFilter('all'); }}>
                  <X size={13} /> Tout effacer
                </button>
              )}
            </motion.div>
          )}
        </div>

        <div className="tms-results-info">
          {filteredTeams.length} équipe{filteredTeams.length !== 1 ? 's' : ''}
        </div>

        {/* ══════════════ LISTE ÉQUIPES ══════════════ */}
        <div className="tms-list">
          {loading ? (
            <>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </>
          ) : filteredTeams.length === 0 ? (
            <div className="tms-empty">
              <Users size={52} />
              <p>
                {searchQuery
                  ? `Aucune équipe correspondant à "${searchQuery}"`
                  : 'Aucune équipe sur la plateforme pour le moment.'
                }
              </p>
            </div>
          ) : (
            filteredTeams.map((team, i) => {
              const count  = team.memberCount || 0;
              const isFull = count >= 5;
              const pct    = Math.min((count / 5) * 100, 100);

              return (
                <motion.div
                  key={team._id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.04, 0.3), type: 'spring', stiffness: 280, damping: 24 }}
                >
                  <Link to={`/team/${team._id}`} className="tms-row-card">
                    {/* Logo */}
                    <div className="tms-row-logo">
                      {team.logo ? (
                        <img src={team.logo} alt={team.name} onError={e => { e.target.style.display = 'none'; }} />
                      ) : (
                        <Shield size={24} />
                      )}
                    </div>

                    {/* Infos */}
                    <div className="tms-row-body">
                      <div className="tms-row-header">
                        <h3 className="tms-row-name">{team.name}</h3>
                        {team.tag && <span className="tms-row-tag">[{team.tag}]</span>}
                        {isFull && <span className="tms-row-badge-full">Complet</span>}
                      </div>
                      <div className="tms-row-meta">
                        <div className="tms-row-members">
                          <div className="tms-row-members-bar">
                            <div
                              className={`tms-row-members-fill${isFull ? ' tms-row-members-fill--full' : ''}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="tms-row-members-text">
                            <Users size={12} /> {count}/5
                          </span>
                        </div>
                        {team.points > 0 && (
                          <span className="tms-row-points">
                            <Trophy size={12} /> {team.points} pts
                          </span>
                        )}
                        {team.wins > 0 && (
                          <span className="tms-row-wins">
                            <Flame size={12} /> {team.wins}W
                          </span>
                        )}
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="tms-row-cta">
                      <span className="tms-row-btn">
                        Voir <ChevronRight size={15} />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })
          )}
        </div>

      </div>
    </motion.div>
  );
}
